import { useCallback, useMemo } from 'react';
import type { FC, MouseEventHandler } from 'react';

import { useIntl } from 'react-intl';

import { quoteComposeById } from '@/mastodon/actions/compose_typed';
import { toggleReblog } from '@/mastodon/actions/interactions';
import { openModal } from '@/mastodon/actions/modal';
import { useStatus } from '@/mastodon/hooks/useStatus';
import { selectStatusConditions } from '@/mastodon/selectors/statuses';
import { useAppDispatch, useAppSelector } from '@/mastodon/store';

import { IconButton } from '../icon_button';

import { boostItemState, messages, quoteItemState } from './boost_button_utils';

interface ReblogButtonProps {
  statusId: string;
  counters?: boolean;
  showLabel?: boolean;
}

export const BoostButton: FC<ReblogButtonProps> = ({
  statusId,
  counters,
  showLabel,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const status = useStatus(statusId);
  const statusState = useAppSelector((state) =>
    selectStatusConditions(state, statusId),
  );
  const { title, meta, iconComponent, disabled } = useMemo(
    () => boostItemState(statusState),
    [statusState],
  );

  const handleClick: MouseEventHandler = useCallback(
    (event) => {
      if (statusState.isLoggedIn) {
        dispatch(toggleReblog(statusId, event.shiftKey));
      } else {
        dispatch(
          openModal({
            modalType: 'INTERACTION',
            modalProps: {
              intent: 'reblog',
              accountId: status?.account,
              url: status?.uri,
            },
          }),
        );
      }
    },
    [dispatch, status, statusId, statusState.isLoggedIn],
  );

  return (
    <IconButton
      disabled={disabled}
      active={!!status?.reblogged}
      title={intl.formatMessage(meta ?? title)}
      label={showLabel ? intl.formatMessage(messages.reblog) : undefined}
      icon='retweet'
      iconComponent={iconComponent}
      className='status__action-bar__button'
      onClick={!disabled ? handleClick : undefined}
      counter={
        counters && status
          ? status.reblogs_count + status.quotes_count
          : undefined
      }
    />
  );
};

export const QuoteButton: FC<ReblogButtonProps> = ({
  statusId,
  counters,
  showLabel,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const status = useStatus(statusId);
  const statusState = useAppSelector((state) =>
    selectStatusConditions(state, statusId),
  );
  const { title, meta, iconComponent, disabled } = useMemo(
    () => quoteItemState(statusState),
    [statusState],
  );

  const handleClick: MouseEventHandler = useCallback(() => {
    if (statusState.isLoggedIn) {
      dispatch(quoteComposeById(statusId));
    } else {
      dispatch(
        openModal({
          modalType: 'INTERACTION',
          modalProps: {
            intent: 'reblog',
            accountId: status?.account,
            url: status?.uri,
          },
        }),
      );
    }
  }, [dispatch, status, statusId, statusState.isLoggedIn]);

  return (
    <IconButton
      disabled={disabled}
      title={intl.formatMessage(meta ?? title)}
      label={showLabel ? intl.formatMessage(messages.quote) : undefined}
      icon='quote-right'
      iconComponent={iconComponent}
      className='status__action-bar__button'
      onClick={!disabled ? handleClick : undefined}
      counter={counters && status ? status.quotes_count : undefined}
    />
  );
};
